import React from 'react'
import {
    Alert,
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    InputBase,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography
} from '@mui/material'

import { TripListCategory, TripStatusTone } from '@/src/types/trips'
import { TripListCard as TripListCardComponent } from '@/src/components/common/trips'
import { TripTitleParts } from '@/src/utils/trips/tripListHelpers'

export interface TripListCard {
    id: string
    tripId?: string
    titleParts: TripTitleParts
    subtitle?: string
    /** Shown under subtitle for LINKED (Cleartrip) bookings */
    bookedByLabel?: string
    bookingSource?: 'MAIN' | 'LINKED'
    dateDay?: string
    dateMonth?: string
    dateLabel?: string
    statusLabel?: string
    statusTone: TripStatusTone
    // classInfo?: string
    // passengerCount?: number
}

export interface TripListTabs {
    value: TripListCategory
    label: string
    count?: number
}

interface TripListDesktopProps {
    heading: string
    tabs: TripListTabs[]
    activeTab: TripListCategory
    onTabChange: (tab: TripListCategory) => void
    cards: TripListCard[]
    isLoading: boolean
    isError: boolean
    errorLabel?: string
    retryLabel?: string
    onRetry: () => void
    emptyState: {
        title: string
        description: string
        actionLabel: string
        onAction: () => void
    }
    onCardClick?: (tripId: string, bookingSource?: 'MAIN' | 'LINKED') => void
    hasMore?: boolean
    isLoadingMore?: boolean
    onLoadMore?: () => void
    loadMoreLabel: string
    loadingLabel: string
}

const SkeletonCard: React.FC = () => (
    <Card elevation={0} sx={{ borderRadius: { xs: 1.5, sm: 2 }, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
            <Box sx={{ display: 'flex' }}>
                <Box
                    sx={{
                        minWidth: { xs: 44, sm: 56 },
                        pr: { xs: 1.5, sm: 2 },
                        mr: { xs: 1.5, sm: 2 },
                        borderRight: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Skeleton variant="text" width={24} sx={{ fontSize: '1.25rem' }} />
                    <Skeleton variant="text" width={24} sx={{ fontSize: '0.6875rem' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Skeleton variant="text" width="55%" sx={{ fontSize: '0.875rem' }} />
                        <Skeleton variant="rounded" width={64} height={20} sx={{ flexShrink: 0 }} />
                    </Stack>
                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.75rem', mt: 0.5 }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
)

const TripListSkeleton: React.FC = () => (
    <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
        {Array.from({ length: 6 }).map((_, idx) => (
            <SkeletonCard key={idx} />
        ))}
    </Stack>
)

/** Shimmer shown below current list while loading more */
const LoadMoreShimmer: React.FC = () => (
    <Stack spacing={{ xs: 1.25, sm: 1.5 }} sx={{ mt: { xs: 1.25, sm: 1.5 } }}>
        {Array.from({ length: 2 }).map((_, idx) => (
            <SkeletonCard key={idx} />
        ))}
    </Stack>
)

const EmptyState: React.FC<{
    title: string
    description: string
    actionLabel: string
    onAction: () => void
}> = ({ title, description, actionLabel, onAction }) => (
    <Box
        sx={{
            textAlign: 'center',
            py: { xs: 3.5, sm: 5 },
            px: { xs: 2, sm: 4 },
            bgcolor: 'background.paper',
            borderRadius: { xs: 1.5, sm: 2 },
            border: 1,
            borderColor: 'divider'
        }}
    >
        <Typography
            variant="h6"
            sx={{
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.125rem' }
            }}
        >
            {title}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                color: 'text.secondary',
                maxWidth: 450,
                mx: 'auto',
                mb: actionLabel ? { xs: 2.5, sm: 3 } : 0,
                fontSize: '0.875rem',
                lineHeight: 1.5
            }}
        >
            {description}
        </Typography>
        {actionLabel && (
            <Button
                variant="contained"
                size="medium"
                onClick={onAction}
                sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem'
                }}
            >
                {actionLabel}
            </Button>
        )}
    </Box>
)

export const TripListDesktop: React.FC<TripListDesktopProps> = ({
    heading,
    tabs,
    activeTab,
    onTabChange,
    cards,
    isLoading,
    isError,
    errorLabel,
    retryLabel,
    onRetry,
    emptyState,
    onCardClick,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    loadMoreLabel,
    loadingLabel
}) => {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: { xs: 1.5, sm: 2 },
                px: { xs: 2, sm: 3 }
            }}
        >
            {/* Header: Title */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    mb: { xs: 2, sm: 3 }
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        lineHeight: 1.3,
                        color: 'text.primary'
                    }}
                >
                    {heading}
                </Typography>

                {/* Search bar disabled for now as requested */}
            </Box>
            <Box
                sx={{
                    py: 2,
                    px: 3,
                    bgcolor: 'common.white',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Tabs: Upcoming / Past */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => onTabChange(newValue)}
                        sx={{
                            minHeight: 40,
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                minHeight: 40,
                                px: { xs: 1.5, sm: 2 },
                                color: 'text.secondary',
                                '&.Mui-selected': theme => ({
                                    fontWeight: 600,
                                    color: alpha(theme.palette.link.main, 0.9),
                                })
                            },
                            '& .MuiTabs-indicator': {
                                height: 2.5,
                                borderRadius: '2px 2px 0 0',
                                backgroundColor: theme => alpha(theme.palette.link.main, 0.9)
                            }
                        }}
                    >
                        {tabs.map(tab => (
                            <Tab key={tab.value} value={tab.value} label={tab.label} />
                        ))}
                    </Tabs>
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        maxHeight: { md: 'calc(100vh - 320px)' },
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        }
                    }}
                >
                    {isLoading && cards.length === 0 && <TripListSkeleton />}

                    {!isLoading && isError && (
                        <Alert
                            severity="error"
                            sx={{
                                borderRadius: 2,
                                border: 1,
                                borderColor: 'divider',
                                mb: 2,
                                fontSize: '0.875rem'
                            }}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={onRetry}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.8125rem',
                                        fontWeight: 500
                                    }}
                                >
                                    {retryLabel || errorLabel}
                                </Button>
                            }
                        >
                            {errorLabel}
                        </Alert>
                    )}

                    {!isLoading && !isError && cards.length === 0 && (
                        <EmptyState
                            title={emptyState.title}
                            description={emptyState.description}
                            actionLabel={emptyState.actionLabel}
                            onAction={emptyState.onAction}
                        />
                    )}

                    {!isError && cards.length > 0 && (
                        <>
                            <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
                                {cards.map(card => (
                                    <TripListCardComponent
                                        key={card.id}
                                        {...card}
                                        onClick={onCardClick}
                                    />
                                ))}
                            </Stack>
                            {isLoadingMore && <LoadMoreShimmer />}
                            {hasMore && onLoadMore && (
                                <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        size="medium"
                                        onClick={onLoadMore}
                                        disabled={isLoadingMore}
                                        sx={{
                                            minWidth: 120,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            px: 3,
                                            py: 1,
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        {isLoadingMore ? loadingLabel : loadMoreLabel}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export type { TripListDesktopProps }
